/**
 * Shared Dentally API functions.
 * Shared Dentally API functions for availability search, patient management, and appointments.
 */
import { callApi } from './apiCall';
import { hasFeatureAccess } from './featurePermission';

// Fetch all Dentally practitioners (paginated, returns full array)
export async function fetchDentallyPractitioners(practiceId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  let all = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;
  while (hasMore) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', perPage);
    params.append('sort_by', 'last_login');
    params.append('sort_direction', 'desc');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(`/api/v1/practices/${practiceId}/dentally/practitioners/?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch Dentally practitioners');
    const list = res?.data?.practitioners || res?.data?.results || [];
    if (!Array.isArray(list) || list.length === 0) {
      hasMore = false;
    } else {
      all = all.concat(list);
      hasMore = !!res.data?.meta?.next_page;
      page += 1;
    }
  }
  return all;
}

// Fetch all Dentally treatments (paginated, returns full array — Dentally caps per_page at ~100)
export async function fetchDentallyTreatments(practiceId, { activeOnly = true } = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  let all = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;
  while (hasMore) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', perPage);
    if (activeOnly) params.append('active', 'true');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(`/api/v1/practices/${practiceId}/dentally/treatments/?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch Dentally treatments');
    const list = res?.data?.treatments || [];
    if (!Array.isArray(list) || list.length === 0) {
      hasMore = false;
    } else {
      all = all.concat(list);
      hasMore = !!res.data?.meta?.next_page;
      page += 1;
    }
  }
  return all;
}

// Fetch calendar diary entries for a month (paginated, returns full array)
export async function fetchDentallyCalendarDiaries(practiceId, monthDate, options = {}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  const toDateStr = (d) => d.toISOString().split('T')[0];
  let all = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const parts = [
      `after=${toDateStr(startDate)}`,
      `before=${toDateStr(endDate)}`,
    ];
    if (options.practitionerId) parts.push(`practitioner_id=${options.practitionerId}`);
    if (options.siteId) parts.push(`site_id=${options.siteId}`);
    if (options.includeInactive) parts.push('include_inactive_practitioners=true');
    parts.push(`page=${page}`, 'per_page=100');
    // eslint-disable-next-line no-await-in-loop
    const res = await callApi(`/api/v1/practices/${practiceId}/dentally/practitioner-diaries/?${parts.join('&')}`);
    const entries = res?.data?.practitioner_diaries || res?.data?.rota_practitioner_diaries || [];
    if (!Array.isArray(entries) || entries.length === 0) {
      hasMore = false;
    } else {
      all = all.concat(entries);
      hasMore = entries.length >= 100;
      page += 1;
    }
  }
  return all;
}

// Create a Dentally appointment
export async function createDentallyAppointment(practiceId, appointmentData) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/create/`,
    'post',
    appointmentData,
    false,
    { timeout: 30000 }
  );
}

// Create a Dentally patient (lead sync) — legacy, kept for reference
export async function createDentallyPatient(practiceId, patientData) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally-patients/`,
    'post',
    { patient: patientData },
    false,
    { timeout: 30000 }
  );
}

// Export a CRM lead to Dentally (new endpoint — backend maps all data)
export async function exportLeadToDentally(leadId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/leads-flat/${leadId}/export_to_dentally/`,
    'post',
    {},
    false,
    { timeout: 30000 }
  );
}

// Fetch available appointment slots from Dentally
export async function fetchDentallyAvailability(practiceId, {
  startTime, finishTime, practitionerIds = [], duration, perPage = 20, page = 1
}) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const params = new URLSearchParams();
  params.append('start_time', startTime);
  params.append('finish_time', finishTime);
  practitionerIds.forEach(id => params.append('practitioner_ids', String(id)));
  if (duration) params.append('duration', String(duration));
  params.append('per_page', String(perPage));
  params.append('page', String(page));
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/availability/?${params.toString()}`
  );
}

// Search Dentally patients by name, email, phone, etc.
export async function searchDentallyPatients(practiceId, query) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/search_patients/?query=${encodeURIComponent(query)}`
  );
}

// Dentally appointment creation with auto-export fallback.
// If the backend returns "Lead has not been exported to Dentally", we export the
// lead first and then retry the appointment creation once.
export async function createDentallyAppointmentAsync(practiceId, appointmentPayload) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const payload = { ...appointmentPayload, state: 'Pending' };
  const url = `/api/v1/practices/${practiceId}/dentally/appointments/create/`;

  const res = await callApi(url, 'post', payload, false, { timeout: 30000 });

  // Check if backend says the lead hasn't been exported yet
  const needsExport = !res?.ok
    && typeof res?.data?.lead_id === 'string'
    && res.data.lead_id.toLowerCase().includes('not been exported');

  if (needsExport && appointmentPayload.lead_id) {
    // Export the lead to Dentally first, then retry
    const exportRes = await exportLeadToDentally(appointmentPayload.lead_id);
    if (exportRes?.ok || exportRes?.status === 200 || exportRes?.status === 201) {
      return callApi(url, 'post', payload, false, { timeout: 30000 });
    }
    return exportRes; // export itself failed — surface that error
  }

  return res;
}

// Update (PATCH) an existing Dentally appointment
export async function updateDentallyAppointment(practiceId, appointmentId, appointmentData) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/${appointmentId}/update/`,
    'patch',
    appointmentData,
    false,
    { timeout: 30000 }
  );
}

// Fetch the primary fee (price) for a Dentally treatment.
// Returns the numeric price_one value, or 0 if no fee exists.
export async function fetchDentallyTreatmentFee(practiceId, dentallyTreatmentId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  if (!practiceId || !dentallyTreatmentId) return 0;
  const res = await callApi(
    `/api/v1/practices/${practiceId}/dentally/fees/?treatment_id=${dentallyTreatmentId}`
  );
  if (!res.ok) return 0;
  const fees = res.data?.fees || [];
  if (fees.length === 0) return 0;
  const price = parseFloat(fees[0].price_one);
  return Number.isFinite(price) && price > 0 ? price : 0;
}

// Fetch appointment reasons configured in Dentally
export async function fetchDentallyAppointmentReasons(practiceId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  const res = await callApi(`/api/v1/practices/${practiceId}/dentally/appointment-reasons/`);
  if (!res.ok) throw new Error('Failed to fetch appointment reasons');
  return res.data?.appointment_reasons || res.data?.results || res.data || [];
}

// Delete (cancel) a Dentally appointment
export async function deleteDentallyAppointment(practiceId, appointmentId) {
  if (!hasFeatureAccess('dentally-integration')) return null;
  return callApi(
    `/api/v1/practices/${practiceId}/dentally/appointments/${appointmentId}/delete/`,
    'delete',
    null,
    false,
    { timeout: 30000 }
  );
}
